import React from 'react';
import { Link } from 'react-router-dom';
import { AppShot } from '../components/AppShot';

const cases = [
  {
    title: 'A client engagement',
    text: 'Give the engagement its own workspace: the board, the pages, the members, and the budget. A guest account can view and comment without seeing the rest of the organization. A share link opens a page for someone who should not have a login.',
    points: [
      'Workspace per client, with its own description and owner',
      'Guest role: view and comment on assigned workspaces',
      'Assistant draft of the weekly client status, held for review',
    ],
  },
  {
    title: 'Several engagements at once',
    text: 'Portfolio is the view for the person who is not inside a single board. It shows which workspace is overdue, who is near the hour estimate, and where the budget has already gone negative.',
    points: [
      'Open, overdue, blocked, and hours in one table',
      'At-risk marker when the workspace is behind',
      'Monday 07:00 UTC email so the rollup is not a meeting',
    ],
  },
  {
    title: 'The work that repeats',
    text: 'When a card hits Blocked, or a new sales item needs a due date, a rule can notify, assign, tag, or comment. The template library covers triage, support ownership, and due-date nudges so you are not writing the first rule from a blank page.',
    points: [
      'Triggers on status, assignment, comments, tags, priority, and due dates',
      'Templates grouped by project, development, support, sales, and general',
      'History of what a rule did',
    ],
  },
  {
    title: 'One person, starting out',
    text: 'The Individual plan is free and stays free. It is one user, two workspaces, and five dimensions, with unlimited work items and the board. Team is the step up when a second person, time tracking, or automation has to be in the account.',
    points: [
      'No card required to open an Individual account',
      'Upgrade from organization settings when the limits get tight',
      'Team is $9.95 per person each month',
    ],
  },
];

export const UseCases: React.FC = () => {
  return (
    <div className="pb-20 pt-28">
      <div className="container-width section-padding">
        <p className="eyebrow">Use cases</p>
        <h1 className="max-w-3xl font-display text-4xl sm:text-5xl">The shapes of work the product is built for.</h1>
        <p className="lede mt-5">
          The demo organization is set up as delivery work: a policy portal, a fleet dashboard, a
          travel ops console, and a field board. These are patterns in that data, not customer quotes.
        </p>

        <div className="mt-10">
          <AppShot
            src="/images/app/workspaces.jpg"
            alt="Demo workspaces named for a policy portal, fleet dashboard, travel ops, and field operations"
          />
        </div>

        <div className="mt-14 grid gap-6 lg:grid-cols-2">
          {cases.map((item) => (
            <article key={item.title} className="surface-card p-6">
              <h2 className="font-display text-2xl">{item.title}</h2>
              <p className="mt-3 text-ink-soft">{item.text}</p>
              <ul className="mt-4 space-y-2 text-sm text-ink">
                {item.points.map((point) => (
                  <li key={point} className="flex gap-2">
                    <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-forest" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <p className="mt-10 text-ink-soft">
          If your work does not look like a client board — a single internal backlog, for example —
          the same workspace still holds the items, the pages, and the time.{' '}
          <Link className="font-semibold text-forest" to="/contact">Ask before you migrate a large board.</Link>
        </p>
      </div>
    </div>
  );
};
